import { SearchType } from '@aws-sdk/client-bedrock-agent-runtime';

export type View = 'chat' | 'table' | 'presentation' | 'courtroom' | 'settings' | 'sandbox' | 'work-product-index' | 'threat-report';

// Helper function to check if a view is a work product view
export const isWorkProductView = (view: View): boolean => {
  return view === 'table' || view === 'presentation' || view === 'courtroom' || view === 'threat-report' || view === 'work-product-index';
};

export type WorkProductType = 'timeline' | 'threat-report';

export type VerificationState = 'verified' | 'unverified';

export type RemoteStorageType = 'GoogleDrive' | 'OneDrive' | 'SharePoint';

export type ThreatReportKey = {
  chatKey: string;
  sessionId: string;
  epochTimeSecs: number;
  requestType: string;
}

export interface WorkProduct {
  id: string;
  type: WorkProductType;
  title: string;
  description?: string;
  isRead: boolean;
  verificationState: VerificationState;
  createdAt?: string;
  createdBy?: string;
  // For timelines, this will be the timeline ID
  // For threat reports, this will be the report ID
  resourceId?: string;
  eventCount?: number;
  status?: string;
}

export type MediaType = 'image' | 'video' | 'audio' | 'pdf' | 'docx' | 'document' | 'url' | 'youtube' | 'website' | 'text' | 'markdown' | 'html' | 'unknown' | 'spreadsheet';

export type JSONValue =
    | string
    | number
    | boolean
    | undefined
    | null
    | { [x: string]: JSONValue }
    | Array<JSONValue>;

export interface JSONObject {
  [x: string]: JSONValue;
}

export interface JSONArray extends Array<JSONValue> { }

export type RemoteStorage = {
  type: string;
  url: string;
}

export type FeatureFlagTypes = 
  JSONValue | JSONObject | JSONArray;

export type SourceContext = Record<string, any>;

export type TimezoneDetails = {
  name: string;
  abbreviation: string | undefined;
  offset: string | undefined;
  offsetInMilliseconds: number | undefined;
}

export type ScopesObject = {
  scopes: string[];
  files: string[];
  excludedScopes: string[];
  excludedFiles: string[];
  contentCategories?: string[];
}

export type EvidenceObject = {
  url?: string;
  mediaType?: MediaType;
  extension?: string;
  isSupportedMedia?: boolean;
  isSupportedExtension?: boolean;
  status?: string;
  sourceInfo?: SourceInfo | null | undefined;
}

export type MediaObject = {
  content?: string;
  mediaType: MediaType;
  mediaSrc?: string;
  mediaUrl?: string;
  s3Url?: string;
  presignedUrl?: string;
  originalUrl?: string;
  isReconstructedUrl?: boolean;
  mediaContext?: SourceContext;
  presignedTranscriptUrl?: string;
  transcriptUrl?: string;
  previewFilePath?: string | undefined;
  derived_object_url?: string;
  sourceInfo?: SourceInfo | null | undefined;
  analysisFilePath?: string | undefined;
  srcToUse?: string | undefined;
  sourceFilePath?: string | undefined;
  sourceUrl?: string | undefined;
};

export type SourceInfo = {
  isAudioVideo?: boolean;
  isImage?: boolean;
  transcriptObjectUrl?: string;
  contentCategories?: string[];
  scope?: string[];
  sourceCaseId?: string;
  sourceObjectUrl?: string;
  sourceContentDigest?: string;
  sourceContentType?: string;
  sourceFileType?: string;
  sourceFileSize?: number;
  sourceUrlHash?: string;
  sourceUrlUuid?: string;
  previewObjectUrl?: string;
  previewContentDigest?: string;
  previewContentType?: string;
  previewFileType?: string;
  previewFileSize?: number;
  previewUrlHash?: string;
  previewUrlUuid?: string;
  downloadObjectUrl?: string;
  downloadContentDigest?: string;
  downloadContentType?: string;
  downloadFileType?: string;
  downloadFileSize?: number;
  downloadUrlHash?: string;
  downloadUrlUuid?: string;
  sourcePageNumber?: number;
  sourcePageCount?: number;
  analysisFilePath?: string | undefined;
}

export type MboxAttachment = {
  attachmentIndex: number;
  attachmentUrl: string;
  fileName: string;
  contentType: string | null;
  contentLength: number;
}

export type MboxEmail = {
  emailIndex: number;
  mboxUrl: string;
  emailUrl: string;
  attachmentUrl: string | null;
  subject: string;
  fromAddress: string;
  toAddresses: string[];
  ccAddresses: string[];
  bccAddresses: string[];
  date: string;
  dateRaw: string;
  attachmentCount: number;
  attachments: MboxAttachment[];
}

export interface SourceObject {
  index?: number | undefined;
  sourceType: string | undefined;
  sourceUrl: string | undefined;
  presignedUrl?: string | undefined;
  originalUrl?: string | undefined;
  isReconstructedUrl?: boolean | undefined;
  truncatedFilePath?: string | undefined;
  pageNumber?: number | undefined;
  sourceContext?: SourceContext | undefined;
  sourceFilePath?: string | undefined;
  presignedTranscriptUrl?: string | undefined;
  transcriptUrl?: string | undefined;
  previewFilePath?: string | undefined;
  derived_object_url?: string;
  status?: string;
  sourceInfo?: SourceInfo | null | undefined;
  analysisFilePath?: string | undefined;
}

export type FileMetadata = {
    objectUrl: string;
    contentDigest?: string;
    contentType?: string;
    fileType?: string;
    fileSize?: number;
    urlUuid: string;
    urlHash: string;
}

export type NewSource = {
  version: string;
  scope: string[];
  sourceCaseId: string;
  index: number;
  content: string;
  location: string;
  contentCategories: string[];
  source: FileMetadata;
  download?: FileMetadata;
  preview?: FileMetadata;
  analysis?: FileMetadata;
  sourcePageNumber?: number;
  sourcePageCount?: number;
  sourceContext?: SourceContext; // This doesn't exist on the response from chat service, no idea where it comes from
  analysisFilePath?: string | undefined;
}

export type Source = {
  index?: number | undefined;
  sourceType?: string | undefined;
  sourceUrl?: string | undefined;
  content?: string;
  location?: string;
  page?: string | undefined;
  pageNumber?: number | undefined;
  sourceFilePath?: string | undefined;
  truncatedFilePath?: string | undefined;
  sourceContext?: SourceContext | undefined;
  presignedTranscriptUrl?: string | undefined;
  transcriptUrl?: string | undefined;
  previewFilePath?: string | undefined;
  derived_object_url?: string;
  sourceInfo?: SourceInfo | null | undefined;
  analysisFilePath?: string | undefined;
};

export type SimpleSource = {
  index: number;
  page: number;
  location: string;
  sourceFilePath: string;
  content: string;
}

export interface Slide {
  approval?: string;
  show: boolean;
  id?: string;
  displayOrder?: number;
  order?: number;
  date?: string;
  displayDate?: string;
  isTimeValid?: boolean;
  time?: string;
  localizedDateTime?: string; // i.e. convert to local timezone
  slideTitle?: string;
  slideSubTitle?: string;
  slideDescription?: string;
  mediaType?: MediaType;
  mediaSrc?: string | null;
  mediaContext?: SourceContext;
  media?: MediaObject[];
  eventType?: string;
  evidenceSrc?: string;
  location?: {
    coordinates: [number, number] | null;
    name: string | null;
    address: string | null;
  };
  sources?: SourceObject[];
  eventLocalTimezone?: string;
  createdAt?: string;
  updatedAt?: string;
}


export interface DateItem {
  date: string;
  displayDate?: string;
  displayOrder?: number;
  events: Slide[];
  show?: boolean;

}

export interface TimelinePatch {
  tags: string[];
  title?: string;
  description?: string;
  media?: MediaObject[] | string | undefined;
  slideTitle?: string;
  slideDescription?: string;
}

export interface LLMConfig {
  refusalMessages: string[],
  textResponsePartDelimiter: string,
  overheadPerPair: number,
  markdownDirective: string,
  promptTemplate: string,
  conversationContext: string,
  promptTemplateBase: string,
  promptTemplateQuery: string,
  promptTemplateWithContext: string,
  orchestrationPromptTemplate: string,
  historyWordLimit: number,
  historyCharLimit: number,
  temperature: number,
  unalignedTemperature: number,
  maxTokens: number,
  numOfResults: number,
  overrideSearchType: SearchType | undefined,
  supportedQueryTransformationConfigTypes: string[],
  passMessages: string[],
  topP: number,
}

// copied from chat/route.ts - may need updated for new timelines
export const SOURCE_TYPES = [
  'DataSyncMT',
  'DataSyncUsb',
  'DataSyncNet',
  'GoogleDrive',
  'OneDrive',
  'Manual',
  'Textract',
  'Unknown',
]

// saving these for reference but using just the /bkb/slow/ for now, since it is in all of them
export const SOURCE_TYPES_TEXTRACT = [
  '/bkb/slow/',
  '/converse_image/',
  '/custom_text_extraction/',
  '/original_content/',
  '/textract/',
  '/word_doc_embedded_images/',
]

export interface TimelineEvent {
  deletedAt: string | null;
  deletedBy: string | null;
  deleted: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  eventEntityId: string | null;
  eventDateTime: string;
  eventLocalTimezone: string;
  isTimeValid: boolean;
  displayOrder: number;
  slideTitle: string;
  slideSubTitle: string | null;
  slideDescription: string;
  location: string | null;
  sources: SourceObject[];
  media: MediaObject[];
  hidden: boolean;
  approved: boolean;
  id: string;
  hiddenAt: string | null;
  hiddenBy: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
}

export interface Timeline {
  deletedAt: string | null;
  deletedBy: string | null;
  deleted: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  title: string;
  description: string;
  tags: string[];
  isGenerated: boolean;
  showTitle: boolean;
  events: TimelineEvent[];
  id: string;
}

export interface TimelineResponse {
  timeline: Timeline;
  sources: SourceObject[];
  media: MediaObject[];
  timelineList: Timeline[];
}

export type StatusEnum = 'active' | 'inactive' | 'pending' | 'suspended';
export type GroupRoleType = 'MEMBER' | 'ADMIN';

export interface UserPreferencesDTO {
  preferences: Record<string, any> | null;
}

export interface GroupMembershipDTO {
  id: string;
  user_id: string;
  group_id: string;
  joined_at: string;
  is_active: boolean;
}

export interface UserTenantRoleDTO {
  id: string;
  user_id: string;
  role_name: string;
  expires_at: string | null;
  is_active: boolean;
}

export interface UserSummaryDTO {
  id: string;
  email: string;
  name: string;
  status: StatusEnum;
  is_admin?: boolean;
}

export interface GroupSummaryDTO {
  id: string;
  name: string;
  description: string | null;
  tenant_id: string;
  status: StatusEnum;
}

export interface CaseSummaryDTO {
  id: string;
  name: string;
  description: string | null;
  tenant_id: string;
  owner_id: string;
  status: StatusEnum;
}

export interface UserDTO {
  id: string;
  email: string;
  name: string;
  status: StatusEnum;
  tenant_id: string;
  last_login: string | null;
  is_admin: boolean;
  preferences: UserPreferencesDTO | null;
  group_memberships: GroupMembershipDTO[];
  groups: GroupSummaryDTO[] | null;
  cases: CaseSummaryDTO[] | null;
  // Cognito status information
  cognito_user_status?: string | null;
  cognito_enabled?: boolean | null;
  cognito_user_create_date?: string | null;
  cognito_days_since_creation?: number | null;
  cognito_needs_password_reset?: boolean | null;
}

export interface UserPreferencesUpsertRequest {
  preferences: Record<string, any> | null;
}

export interface CreateOrganizationRequest {
  name: string;
  contact_email?: string;
  auth_architecture: string;
  domain?: string;
  status: string;
  cognito_user_pool_arn?: string;
  cognito_domain?: string;
  app_client_id?: string;
}

export interface OrganizationDTO {
  id: string;
  external_id: string;
  name: string;
  contact_email?: string;
  auth_architecture: string;
  domain?: string;
  status: string;
  cognito_user_pool_arn?: string;
  cognito_domain?: string;
  app_client_id?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  tenant_count?: number;
  /** Whether users can add tenants (current org and knowledge base ready). */
  can_add_tenants?: boolean;
  /** True when this org is the current org in tenant_pool_state. */
  is_current_organization?: boolean;
  /** From tenant_pool_state when this org is current. */
  knowledge_base_ready?: boolean | null;
  /** UI status line from backend (e.g. "Current · Accepting tenants"). */
  status_message?: string | null;
  /** Hint for UI styling: success (green), warning (amber), muted (slate). */
  status_class?: 'success' | 'warning' | 'muted' | null;
}

export interface OrganizationListResponse {
  organizations: OrganizationDTO[];
  total: number;
}

export interface UpdateOrganizationRequest {
  name: string;
  contact_email?: string;
  auth_architecture: string;
  domain?: string;
  status: string;
  cognito_user_pool_arn?: string;
  cognito_domain?: string;
  app_client_id?: string;
}

export interface TenantDTO {
  id: string;
  external_id?: string;
  organization_id: string;
  tenant_name: string;
  tenant_code: string;
  timezone: string;
  contact_email?: string;
  status: string;
  s3_path?: string;
  cognito_user_pool_arn?: string;
  cognito_domain?: string;
  app_client_id?: string;
  avp_store_id?: string;
  avp_identity_source_id?: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
  banners: string[];
  remote_storage_type?: RemoteStorageType;
  remote_storage_url?: string;
  multi_tenant_friendly_name?: string;
  organization: OrganizationDTO;
}

export interface CreateTenantRequest {
  organization_id: string;
  tenant_name: string;
  tenant_code: string;
  timezone: string;
  contact_email?: string;
  banners: string[];
  remote_storage_type?: RemoteStorageType;
  remote_storage_url?: string;
  multi_tenant_friendly_name?: string;
}

export interface TenantListResponse {
  tenants: TenantDTO[];
  total: number;
}

export interface UpdateTenantRequest {
  tenant_name: string;
  timezone: string;
  contact_email?: string;
  banners: string[];
  remote_storage_type?: RemoteStorageType;
  remote_storage_url?: string;
  multi_tenant_friendly_name?: string;
}

export const AUTH_ARCHITECTURE_OPTIONS = [
  { value: 'cognito-single-pool', label: 'Cognito Single Pool' },
  { value: 'cognito-multi-tenant', label: 'Cognito Multi-Tenant' },
  { value: 'okta', label: 'Okta' },
];


export const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern' },
  { value: 'America/Denver', label: 'Mountain' },
  { value: 'America/Chicago', label: 'Central' },
  { value: 'America/Los_Angeles', label: 'Pacific' },
];

// ── CSM Tenant Access ─────────────────────────────────────────────────────────

export interface CSMTenantAccessDTO {
  id: string;
  csm_user_id: string;
  email?: string | null;
  organization_id?: string | null;
  tenant_id: string;
  is_active: boolean;
  granted_at: string;
  created_at: string;
  updated_at: string;
  granted_by?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
}

export interface CSMTenantAccessListResponse {
  csm_list: CSMTenantAccessDTO[];
  total: number;
}

export interface CSMBulkGrantAccessResult {
  csm_email: string;
  csm_user_id: string;
  success: boolean;
  action?: 'created' | 'reactivated' | 'already_active' | null;
  error?: string | null;
}

export interface CSMBulkGrantAccessResponse {
  tenant_id: string;
  total: number;
  granted: number;
  skipped: number;
  failed: number;
  results: CSMBulkGrantAccessResult[];
}

export interface CSMUserSummaryDTO {
  id: string;
  user_id: string;
  email: string;
  is_active: boolean;
  accessible_tenants: string[];
}

export interface ActiveCSMUserListResponse {
  csm_list: CSMUserSummaryDTO[];
  total: number;
}